import { I18n } from "@assets/strings";
import React from "react";
import { Link } from "react-router";
import styled, { css } from "styled-components";
import defaultShareroom from "@assets/images/default_shareroom.png";
import { Path } from "@core/routing/path";
import { Group } from "@domain/group/group";
import { ImageVersions } from "@domain/media/dtos";
import { useHover, useIsMobile } from "@utils/hooks";
import { theme } from "@ui/styles/theme";
import { alpha, gap, mobile } from "@ui/styles/utils";
import { BackgroundImage } from "@ui/components/backgroundImage";
import { BadgeLabel } from "@ui/components/badgeLabel";
import { CategoryLabel } from "@ui/components/categoryLabel";
import { Clamp } from "@ui/components/containers/clamp";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { CommunityIcon, ShareroomIcon, StarIcon } from "@ui/components/icons";
import { row } from "@ui/components/layout";
import { MembersButton } from "@ui/components/profile/community/membersButton";
import { JoinGroupThumbnailButton } from "./joinGroupThumbnailButton";
import { DiscoverShareroomThumbnailButton } from "@ui/components/group/shareroom/discoverShareroomThumbnailButton";
import { ShareroomPricing } from "./shareroomPricing";
import { ShareroomThumbnailMenu } from "./shareroomThumbnailMenu";

interface ShareroomThumbnailProps extends React.HTMLAttributes<HTMLDivElement> {
	shareroom: Group;
	small?: boolean;
	border?: boolean;
}
export const ShareroomThumbnail: React.FC<ShareroomThumbnailProps> = ({ shareroom, small, border, ...htmlProps }) => {
	const {
		banner,
		name,
		description,
		administrators: teachersList,
		categories,
		memberCounts,
		license,
		feedbacks: { meanScore },
	} = shareroom;
	const memberStudentsCount = memberCounts.MEMBER ?? 0;
	const { ref, isHovered } = useHover();
	const isMobile = useIsMobile();

	return (
		<Container {...htmlProps} border={border} ref={ref}>
			<Content>
				<Link to={Path.Shareroom(shareroom)}>
					<StyledBackgroundImage
						image={banner}
						defaultUrl={!banner ? defaultShareroom : undefined}
						size={ImageVersions.MEDIUM}
						backdrop
						isHovered={isHovered}
					/>

					<Infos>
						<Title size={1} small={small}>
							{name}
						</Title>
						{!isMobile && <Description size={2}>{description}</Description>}
						{memberStudentsCount > 0 && (
							<Feature>
								<MembersCount small={small}>
									<CommunityIcon color={theme.colors.white} width={18} />
									<Members>{memberStudentsCount}</Members>
								</MembersCount>
								{meanScore ? (
									<Score>
										<StarIcon width={14} height={14} />
										<MeanScore>{meanScore.toFixed(1)}</MeanScore>
									</Score>
								) : null}
							</Feature>
						)}
						<TeachersLabel small={small}>
							<b>{I18n.t("group.SHAREROOM.thumbnail.animated_by")}</b>
						</TeachersLabel>
						<MembersButton
							sample={teachersList}
							count={4}
							spacing={small ? 5 : -13}
							imageSize={small ? "default" : "M"}
						/>
						<Line>
							{shareroom.license === "PREMIUM" ? (
								<DiscoverShareroomThumbnailButton shareroom={shareroom} />
							) : (
								<JoinGroupThumbnailButton group={shareroom} />
							)}
							{license === "PREMIUM" ? <ShareroomPricing shareroom={shareroom} /> : null}
						</Line>
					</Infos>
				</Link>
			</Content>
			{!isMobile && (
				<Badge
					isHovered={isHovered}
					hoverWidth={162}
					renderIcons={() => <ShareroomIcon />}
					badgeTitle={I18n.t("group.SHAREROOM.thumbnail_badge")}
				/>
			)}
			{categories[0] && categories[0] !== "OTHERS" && (
				<Category
					category={categories[0]}
					isMobile={isMobile}
					isSmall={isMobile}
					renderIcons={() => <ShareroomIcon color={theme.colors.white} />}
				/>
			)}
			<StyledMenu shareroom={shareroom} />
		</Container>
	);
};

const Container = styled(RoundedBlock).withConfig({
	shouldForwardProp: (prop) => prop !== "border",
})<{ border?: boolean }>`
	position: relative;
`;

const Content = styled(RoundedBlock)`
	max-height: 340px;
	position: relative;
	display: flex;
	flex-direction: column;
	align-items: center;
	border-radius: 13px;
	overflow: hidden;

	> a {
		width: 100%;
		height: 100%;
	}

	@media (max-width: 900px) {
		padding-top: 0;
	}
`;

const Badge = styled(BadgeLabel)`
	position: absolute;
	top: 0px;
	left: 20px;
`;

const Category = styled(CategoryLabel).withConfig({
	shouldForwardProp: (prop) => prop !== "isMobile",
})<{ isMobile?: boolean }>`
	position: absolute;

	${({ isMobile }) =>
		isMobile
			? css`
					left: 10px;
					top: 10px;
				`
			: css`
					right: 64px;
					top: 10px;
				`}
`;

const StyledMenu = styled(ShareroomThumbnailMenu)`
	position: absolute;
	right: 24px;
	top: 10px;
	background-color: ${({ theme }) => alpha(theme.colors.backgroundPrimary, 0.98)};

	@media ${mobile} {
		right: 10px;
		top: 10px;
	}
`;

const Infos = styled.div`
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	padding: 24px;
	color: ${({ theme }) => theme.colors.white};
	// pointer-events: none;
	${gap(15)};
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	justify-content: flex-end;

	@media ${mobile} {
		padding: 10px;
		${gap(5)};
	}
`;

const Title = styled(Clamp).withConfig({
	shouldForwardProp: (prop) => prop !== "small",
})<{ small?: boolean }>`
	${({ theme, small }) => (small ? theme.textStyles.text : theme.textStyles.subtitle)};
	color: ${theme.colors.white};
	position: absolute;
	top: 60px;
`;

const Description = styled(Clamp)`
	font-weight: 500;
	position: absolute;
	top: 90px;
`;

const MembersCount = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "small",
})<{ small?: boolean }>`
	${row(0, undefined, "center")};
	${({ theme, small }) => (small ? theme.textStyles.small : theme.textStyles.secondary)};
	gap: 4px;
`;
const Members = styled.div`
	color: ${({ theme }) => theme.colors.white};
	font-weight: 600;
`;
const Feature = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "small",
})<{ small?: boolean }>`
	${row(0, undefined, "center")};
	gap: 20px;
`;
const TeachersLabel = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "small",
})<{ small?: boolean }>`
	${({ theme, small }) => (small ? theme.textStyles.small : theme.textStyles.primary)};
	color: ${theme.colors.white};
	font-weight: 700;
`;

const StyledBackgroundImage = styled(BackgroundImage)`
	height: 342px;
	@media ${mobile} {
		height: 235px;
	}
`;
const Line = styled.div`
	${row(0, "space-between", "center")}
	width: 100%;
`;
const Score = styled.div`
	${row(0, undefined, "center")};
	gap: 4px;

	@media (max-width: 900px) {
		align-self: flex-start;
	}
`;
const MeanScore = styled.div`
	color: ${({ theme }) => theme.colors.white};
	font-weight: 200;
`;

// const Review = styled.div`
//     ${row(0, undefined, "center")};
// 	gap: 4px;

// 	@media (max-width: 900px) {
// 		align-self: flex-start;
// 	}
// `;
// const FeedbackCount = styled.div`
//     color: ${({ theme }) => theme.colors.white};
//     font-weight: 200;
// `
